import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Megaphone } from 'lucide-react'
import { api } from '@/util/api'
import { EventWorkspaceShell } from '@/components/EventWorkspaceShell'
import { EventViewSwitcher } from '@/components/EventViewSwitcher'
import { EventWorkspaceActions } from '@/components/organisms/EventWorkspaceActions'
import { SponsorsSection } from '@/components/SponsorsSection'
import { OnboardingEditor } from '@/components/OnboardingEditor'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { Button, Notice, Tabs } from '@/components/play'
import { publicEventPath } from '@/lib/eventLinks'
import { workspaceError } from '@/lib/workspace'
import type { EventResource } from '@/types/events'

/**
 * Standalone admin surface for managing an event's sponsor campaigns.
 * Mounted at `/events/:id/sponsors` so it sits as a peer to the canvas,
 * map, and multiview surfaces rather than crowding the main camera grid.
 */
export function SponsorsPage() {
  const { id } = useParams<{ id: string }>()
  const [event, setEvent] = useState<EventResource | null>(null)
  const [loadError, setLoadError] = useState('')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoadError('')
    api
      .get<{ data: EventResource }>(`/events/${id}`)
      .then((res) => {
        if (!cancelled) setEvent(res.data)
      })
      .catch((failure) => {
        if (!cancelled) setLoadError(workspaceError(failure, 'Your event couldn’t load. Try again.'))
      })
    return () => {
      cancelled = true
    }
  }, [id, attempt])

  if (!id) return null

  return (
    <EventWorkspaceShell
      backTo={`/events/${id}`}
      backLabel={event?.attributes.name ?? 'Your event'}
      title={
        <div className="flex min-w-0 items-center gap-3">
          <Megaphone size={18} className="shrink-0 text-neutral-10" aria-hidden="true" />
          <h1 className="truncate text-xl font-semibold text-neutral-10">Sponsors</h1>
        </div>
      }
      meta={<span>Tag-based sponsor campaigns</span>}
      actions={
        <EventWorkspaceActions
          eventId={id}
          eventName={event?.attributes.name ?? ''}
          publicPageEnabled={event ? event.attributes.template_key !== 'none' : undefined}
          publicPath={publicEventPath(event ?? { id })}
          showConnectionStatus={false}
        />
      }
      workspaceSwitcher={<EventViewSwitcher eventId={id} active="sponsors" />}
    >
      <div className="app-page max-w-6xl space-y-6">
        {loadError && (
          <Notice
            tone="error"
            alert
            action={<Button variant="secondary" size="compact" onClick={() => setAttempt((current) => current + 1)}>Try again</Button>}
          >
            {loadError}
          </Notice>
        )}
        <Tabs
          label="Sponsors"
          items={[
            {
              value: 'campaigns',
              label: 'Campaigns',
              content: (
                <div className="pt-4">
                  <SponsorsSection eventId={id} />
                </div>
              ),
            },
            {
              value: 'onboarding',
              label: 'Onboarding',
              content: (
                <div className="max-w-3xl pt-4">
                  {/* The guide opens once the event has loaded, so a save never replaces it with blank fields. */}
                  {event ? (
                    <OnboardingEditor eventId={id} event={event} onSaved={setEvent} />
                  ) : (
                    !loadError && <ContentSkeleton label="Loading the onboarding guide" variant="form" />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </EventWorkspaceShell>
  )
}
